import React, { useState, useRef } from "react";
import { apiRequest } from "../utils/ApiRequest";
import { Helmet } from "react-helmet-async";

const talentFormBkg = "/assets/talent-form-bkg.jpg";

const inputCls = "px-[15px] py-3 rounded-lg border border-[#ccc] text-base w-full box-border mb-5 font-[inherit]";

const LasgidiGotTalent = () => {
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", over18: null, guardianName: "", guardianContact: "",
    confirmAge: false, videoFile: null, videoLink: "", talentType: "", otherTalent: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const showGuardianField = formData.over18 === false;

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") { setFormData((prev) => ({ ...prev, [name]: checked })); }
    else if (type === "file") {
      const file = files[0]; if (!file) return;
      if (file.size > 100 * 1024 * 1024) { alert("The selected file exceeds 100MB. Please upload a smaller video."); e.target.value = ""; setFormData((prev) => ({ ...prev, videoFile: null })); return; }
      setFormData((prev) => ({ ...prev, videoFile: file }));
    } else { setFormData((prev) => ({ ...prev, [name]: value })); }
  };

  const handleTalentChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, talentType: value, otherTalent: value !== "Other" ? "" : prev.otherTalent }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) { setError("Name, email, and phone are required."); return; }
    if (!formData.over18 && (!formData.guardianName.trim() || !formData.guardianContact.trim())) { setError("Please provide guardian information."); return; }
    if (!formData.confirmAge) { setError("Please confirm that the information provided is accurate."); return; }
    setLoading(true); setError("");
    try {
      let uploadedVideoUrl = "";
      if (formData.videoFile) {
        const videoForm = new FormData(); videoForm.append("videoFile", formData.videoFile);
        const uploadResult = await apiRequest({ script: "upload-video", data: videoForm, isFormData: true });
        if (uploadResult.success) { uploadedVideoUrl = uploadResult.url; } else { throw new Error(uploadResult.message || "Video upload failed."); }
      }
      const submissionData = new FormData();
      submissionData.append("name", formData.name.trim()); submissionData.append("email", formData.email.trim()); submissionData.append("phone", formData.phone.trim());
      const talentValue = formData.talentType === "Other" ? formData.otherTalent.trim() : formData.talentType;
      submissionData.append("talentType", talentValue); submissionData.append("over18", formData.over18 ? "true" : "false");
      submissionData.append("confirmAge", formData.confirmAge ? "1" : ""); submissionData.append("guardianName", formData.guardianName.trim());
      submissionData.append("guardianContact", formData.guardianContact.trim()); submissionData.append("videoFile", uploadedVideoUrl); submissionData.append("videoLink", formData.videoLink.trim());
      const result = await apiRequest({ script: "lasgidi-got-talent", data: submissionData, isFormData: true });
      if (result.success) {
        alert("Application submitted successfully!");
        setFormData({ name: "", email: "", phone: "", over18: null, guardianName: "", guardianContact: "", confirmAge: false, videoFile: null, videoLink: "", talentType: "", otherTalent: "" });
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else { setError(result.message || "Submission failed."); }
    } catch (err) { console.error("Submission Error:", err); setError(err.message || "An error occurred while submitting the form."); }
    finally { setLoading(false); }
  };

  return (
    <div className="relative py-10 text-[#333] page-container overflow-hidden standard-padding pb-24">
      <Helmet>
        <title>Lasgidi Got Talent | Registration</title>
        <link rel="preload" as="image" href={talentFormBkg} fetchpriority="high" />
      </Helmet>

      <img 
        src={talentFormBkg} 
        alt="" 
        className="absolute inset-0 w-full h-full object-cover -z-10"
        fetchpriority="high"
        loading="eager"
      />
      {loading && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[9999]">
          <div className="border-4 border-white/30 border-t-white rounded-full w-10 h-10 animate-spin" />
        </div>
      )}

      <form
        className="flex flex-col bg-[#f8f8f8] w-full max-w-[700px] mx-auto p-[30px] rounded-[15px] shadow-[rgba(196,153,13,0.6)_0px_12px_36px_0px] max-[768px]:p-5"
        onSubmit={handleSubmit}
        encType="multipart/form-data"
      >
        <div className="mb-5">
          <h1 className="text-center text-[2.5rem] mb-2.5 text-black">Lasgidi Got Talent: Registration</h1>
          <p className="text-center text-lg mb-[30px] font-bold">Fill out the form below to register your interest before the event date is announced.</p>
        </div>

        {error && <p className="text-[#d63447] text-base font-bold mb-3 text-center">{error}</p>}

        {[
          { label: "Name:", type: "text", name: "name", value: formData.name },
          { label: "Email:", type: "email", name: "email", value: formData.email },
          { label: "Phone:", type: "tel", name: "phone", value: formData.phone },
        ].map(({ label, type, name, value }) => (
          <React.Fragment key={name}>
            <label className="font-bold mb-1 text-base">{label}</label>
            <input type={type} name={name} value={value} onChange={handleChange} required className={inputCls} />
          </React.Fragment>
        ))}

        <label className="font-bold mb-1 text-base">What talent will you display?</label>
        <select name="talentType" value={formData.talentType} onChange={handleTalentChange} required className={inputCls}>
          <option value="" disabled>Select your talent</option>
          {["Singing", "Dance", "Poetry / Spoken Word", "Comedy", "Acting", "Instrumental Performance", "Art", "Other"].map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        {formData.talentType === "Other" && (
          <>
            <label className="font-bold mb-1 text-base">Please specify your talent:</label>
            <input type="text" name="otherTalent" value={formData.otherTalent} onChange={handleChange} required className={inputCls} />
          </>
        )}

        <label className="font-bold mb-1 text-base">Upload Video Clip (100MB max) or YouTube Link:</label>
        <input type="file" name="videoFile" accept="video/*" onChange={handleChange} ref={fileInputRef} className={inputCls} />
        <input type="url" name="videoLink" value={formData.videoLink} placeholder="YouTube Link" onChange={handleChange} className={inputCls} />

        <label className="inline-block font-medium my-2.5 text-base">Are you 18 years or older?</label>
        <div className="flex gap-4 mb-2">
          {[{ id: "ageYes", label: "Yes", val: true }, { id: "ageNo", label: "No", val: false }].map(({ id, label, val }) => (
            <React.Fragment key={id}>
              <input type="radio" id={id} name="over18" className="hidden" checked={formData.over18 === val}
                onChange={() => setFormData({ ...formData, over18: val, ...(val ? { guardianName: "", guardianContact: "" } : {}) })} />
              <label htmlFor={id} className="px-5 py-2 border border-[#ccc] rounded-[25px] cursor-pointer text-sm select-none transition-all duration-200 hover:bg-[#f0f0f0]">{label}</label>
            </React.Fragment>
          ))}
        </div>

        {showGuardianField && (
          <>
            <label className="font-bold mb-1 text-base">Parent/Guardian Name:</label>
            <input type="text" name="guardianName" value={formData.guardianName} onChange={handleChange} required className={inputCls} />
            <label className="font-bold mb-1 text-base">Parent/Guardian Contact:</label>
            <input type="text" name="guardianContact" value={formData.guardianContact} onChange={handleChange} required className={inputCls} />
          </>
        )}

        <label className="flex items-center gap-2 mb-5 cursor-pointer">
          <input type="checkbox" name="confirmAge" checked={formData.confirmAge} onChange={handleChange} className="scale-[1.2] mr-2.5" />
          I confirm that the information provided is accurate.
        </label>

        <button type="submit" disabled={loading} className="w-max mx-auto my-5 px-5 py-2.5 bg-brand-orange text-black font-bold border-none text-base cursor-pointer rounded transition-opacity duration-300 hover:opacity-70 disabled:opacity-50 font-[inherit]">
          {loading ? <span className="inline-block w-5 h-5 border-[3px] border-white border-t-white/30 rounded-full animate-spin" /> : "Submit Application"}
        </button>
      </form>
    </div>
  );
};

export default LasgidiGotTalent;
